import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { map } from 'lodash';
import React, { FunctionComponent, useState } from 'react';

import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';

import {
  CaseOrganizationExecutionMode,
  caseOrganizationHandlingLabels,
  CaseOrganizationPlan,
} from './caseOrganizationPlan';

export const WHOLE_STUDY_CONFIRMATION_TEXT = 'REPLACE WHOLE STUDY';

interface CaseOrganizationConfirmationDialogProps {
  open: boolean;
  plan: CaseOrganizationPlan;
  onClose: () => void;
  onApplyCaseOrganization: () => void;
}

const getReviewTitle = (executionMode: CaseOrganizationExecutionMode) => {
  if (executionMode === 'wholeStudyReplacement') {
    return 'Review study case-assignment replacement';
  }

  if (executionMode === 'selectedCaseOverride') {
    return 'Review cases in the plan';
  }

  return 'Review slide organization';
};

const getPrimaryActionLabel = (executionMode: CaseOrganizationExecutionMode) => {
  if (executionMode === 'wholeStudyReplacement') {
    return 'Replace study case assignments';
  }

  if (executionMode === 'selectedCaseOverride') {
    return 'Apply case changes';
  }

  return 'Save changes';
};

const PlanSummary: FunctionComponent<{ plan: CaseOrganizationPlan }> = ({ plan }) => {
  const summaryItems = [
    { label: 'Cases created', count: plan.createdCases.count },
    { label: 'Cases updated', count: plan.updatedCases.count },
    { label: 'Slides assigned', count: plan.assignedSlides.count },
    { label: 'Slides moved', count: plan.movedSlides.count },
    { label: 'Cases made empty', count: plan.emptiedCases.count },
    { label: 'Cross-scope slides', count: plan.crossScopeSlides.count },
    { label: 'Cases removed from study', count: plan.replacedCases.count },
  ].filter(({ count }) => count > 0);

  if (plan.isNoOp) {
    return <Alert severity="info">No changes are available to apply.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <List dense disablePadding aria-label="Slide organization consequences">
        {map(summaryItems, ({ label, count }) => (
          <ListItem key={label} disableGutters>
            <ListItemText primary={label} />
            <Typography variant="body2" fontWeight={600}>
              {count}
            </Typography>
          </ListItem>
        ))}
      </List>
      {plan.movedSlides.count > 0 ? (
        <Box>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Slides moving between cases
          </Typography>
          <DataGrid
            rows={plan.movedSlides.items}
            getRowId={(row) => row.slideId}
            columns={[
              { field: 'slideLabel', headerName: 'Slide', flex: 1 },
              { field: 'sourceCaseLabel', headerName: 'Current case', flex: 1 },
              { field: 'targetCaseLabel', headerName: 'New case', flex: 1 },
            ]}
            autoHeight
            disableRowSelectionOnClick
            hideFooter
          />
          {plan.movedSlides.omittedCount > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {plan.movedSlides.omittedCount} more moved slides are included.
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {plan.crossScopeSlides.count > 0 ? (
        <Box>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Cross-scope assignment details
          </Typography>
          <List dense disablePadding aria-label="Cross-scope assignment details">
            {map(plan.crossScopeSlides.items, (assignment) => (
              <ListItem key={assignment.slideId} disableGutters>
                <ListItemText
                  primary={assignment.slideLabel}
                  secondary={`Source: ${assignment.sourceLabName || assignment.sourceLabId || 'Unknown lab'} / ${
                    assignment.sourceStudyId || 'Unknown study'
                  }. Target: ${assignment.targetLabId} / ${assignment.targetStudyId} / ${assignment.targetCaseLabel}.`}
                />
              </ListItem>
            ))}
          </List>
          {plan.crossScopeSlides.omittedCount > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {plan.crossScopeSlides.omittedCount} more cross-scope slides are included.
            </Typography>
          ) : null}
          <Alert severity="info">
            Source provenance is retained. After assignment, each slide is visible to users who can access its target
            case in the target study.
          </Alert>
        </Box>
      ) : null}
      {plan.emptiedCases.count > 0 ? (
        <Alert severity="warning">
          {plan.emptiedCases.count === 1
            ? '1 case will become empty and be deleted.'
            : `${plan.emptiedCases.count} cases will become empty and be deleted.`}
        </Alert>
      ) : null}
    </Stack>
  );
};

export const CaseOrganizationConfirmationDialog: FunctionComponent<
  React.PropsWithChildren<CaseOrganizationConfirmationDialogProps>
> = ({ open, plan, onClose, onApplyCaseOrganization }) => {
  const [wholeStudyConfirmation, setWholeStudyConfirmation] = useState('');
  const executionMode = plan.executionMode;
  const activePlan = plan;
  const requiresTypedConfirmation = executionMode === 'wholeStudyReplacement';
  const primaryActionDisabled =
    activePlan.isNoOp || (requiresTypedConfirmation && wholeStudyConfirmation !== WHOLE_STUDY_CONFIRMATION_TEXT);

  const resetAndClose = () => {
    setWholeStudyConfirmation('');
    onClose();
  };

  const applyCaseOrganization = () => {
    resetAndClose();
    onApplyCaseOrganization();
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="case-organization-review-title"
    >
      <DialogChromeHeader
        title={getReviewTitle(executionMode)}
        subtitle="Confirm what will change before saving this slide organization."
        titleId="case-organization-review-title"
        closeButtonAriaLabel="Close slide organization review"
        onClose={resetAndClose}
      />
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2">{caseOrganizationHandlingLabels[executionMode]}</Typography>
          <PlanSummary plan={activePlan} />
          {executionMode === 'selectedCaseOverride' ? (
            <Alert severity="info">
              Cases not listed in the plan are retained unless slides move out of them. Moving all slides out of a case
              within this study deletes that empty case.
            </Alert>
          ) : null}
          {executionMode === 'wholeStudyReplacement' ? (
            <Stack spacing={2}>
              <Alert severity="warning">
                This deletes {activePlan.replacedCases.count} existing{' '}
                {activePlan.replacedCases.count === 1 ? 'case whose label is' : 'cases whose labels are'} absent from
                the plan, containing {activePlan.replacedSlidesCount}{' '}
                {activePlan.replacedSlidesCount === 1 ? 'slide' : 'slides'}, before applying the case batches. Cases
                listed in the plan are created or updated. Deletion and later batches can complete independently, so a
                failure may leave partial changes.
              </Alert>
              <TextField
                fullWidth
                label={`Type ${WHOLE_STUDY_CONFIRMATION_TEXT} to confirm`}
                value={wholeStudyConfirmation}
                onChange={(event) => setWholeStudyConfirmation(event.target.value)}
                autoComplete="off"
              />
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        <Button color="inherit" onClick={resetAndClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={executionMode === 'wholeStudyReplacement' ? 'error' : 'primary'}
          sx={dialogPrimaryActionSx}
          disabled={primaryActionDisabled}
          onClick={applyCaseOrganization}
        >
          {getPrimaryActionLabel(executionMode)}
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
